import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom, type Observable } from 'rxjs';
import { catchError, map, of, tap } from 'rxjs';

import { AUTH_CONFIG } from './auth-config.token';
import { AuthStore } from './auth.store';
import type { AuthedUser, LoginOrMfa, LoginResponse } from './models';
import { isMfaRequired } from './models';

/**
 * Auth I/O. Talks to `HttpClient` directly (rather than going through
 * `ApiClientService`) for three reasons:
 *
 *   1. Auth endpoints need `withCredentials: true` so the refresh
 *      cookie rides on the request, and we want that scoped — not
 *      every shared-api call.
 *   2. We need to be able to issue requests before/without an access
 *      token (login, refresh) without the interceptor's 401-recovery
 *      logic competing.
 *   3. The backend's auth controllers intentionally return the bare
 *      `LoginResponse` / `AuthedUser` shape rather than the standard
 *      `{ data, meta }` envelope — `ApiClientService` would try to
 *      unwrap a non-existent `data` key.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(AUTH_CONFIG);
  private readonly store = inject(AuthStore);

  /**
   * Submits credentials. Returns either the normal login response
   * (already applied to the store) or the MFA-pending response (NOT
   * applied — caller must collect the TOTP and call `verifyMfa`).
   */
  login(email: string, password: string): Observable<LoginOrMfa> {
    return this.http
      .post<LoginOrMfa>(this.config.loginPath, { email, password }, { withCredentials: true })
      .pipe(
        tap((result) => {
          if (!isMfaRequired(result)) {
            this.store.setSession(result.accessToken, result.user);
          }
        }),
      );
  }

  /**
   * Request a password-reset email. The backend always responds 2xx and
   * only actually sends a link for an active account, so the caller shows
   * the same confirmation regardless (no account-existence leak). Throws
   * only on network/5xx errors.
   */
  requestPasswordReset(email: string): Observable<void> {
    const path = this.config.passwordResetRequestPath;
    if (!path) throw new Error('passwordResetRequestPath is not configured for this app.');
    return this.http
      .post<void>(path, { email }, { withCredentials: true })
      .pipe(map(() => undefined));
  }

  /**
   * Complete a password reset with the token from the emailed link and the
   * user's chosen new password.
   */
  confirmPasswordReset(token: string, newPassword: string): Observable<void> {
    const path = this.config.passwordResetConfirmPath;
    if (!path) throw new Error('passwordResetConfirmPath is not configured for this app.');
    return this.http
      .post<void>(path, { token, newPassword }, { withCredentials: true })
      .pipe(map(() => undefined));
  }

  /**
   * Boot-time + on-401 refresh. Uses the refresh cookie alone — no
   * body. Returns the new `LoginResponse` and updates the store.
   * Surfaces errors so callers can decide whether to drop the user
   * to /login.
   */
  refresh(): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(this.config.refreshPath, {}, { withCredentials: true })
      .pipe(tap((result) => this.store.setSession(result.accessToken, result.user)));
  }

  /**
   * Revoke the refresh cookie + clear the store. Always clears the
   * store locally even if the server call fails — a stale session is
   * worse than a noisy logout error.
   */
  logout(): Observable<void> {
    return this.http.post<void>(this.config.logoutPath, {}, { withCredentials: true }).pipe(
      map(() => undefined),
      catchError(() => of(undefined)),
      tap(() => this.store.setUnauthenticated()),
    );
  }

  /**
   * Re-fetch the user (e.g. after a permission flip on the backend).
   * Boot path does NOT call this — refresh already returns the user.
   */
  fetchMe(): Observable<AuthedUser> {
    return this.http
      .get<AuthedUser>(this.config.mePath, { withCredentials: true })
      .pipe(tap((user) => this.store.setUser(user)));
  }

  /**
   * Promise-flavoured boot probe. Used by `provideAuthInitializer` to
   * gate the router until we know if the refresh cookie is good.
   * Swallows errors — a failed refresh just means unauthenticated.
   *
   * Idempotent: concurrent callers (app initializer + route guard
   * firing back-to-back at startup, the interceptor recovering from
   * a 401 while a guard is also probing) share the same in-flight
   * Promise. Without this, two simultaneous refresh requests can
   * rotate the cookie, race in the store, and leave the UI half-
   * loaded — which manifests as "click around to make components
   * appear".
   */
  private inflightBootRefresh: Promise<void> | null = null;

  async bootRefresh(): Promise<void> {
    if (this.inflightBootRefresh) return this.inflightBootRefresh;
    const probe = (async () => {
      try {
        await firstValueFrom(this.refresh());
      } catch {
        this.store.setUnauthenticated();
      } finally {
        this.inflightBootRefresh = null;
      }
    })();
    this.inflightBootRefresh = probe;
    return probe;
  }
}
